'use client'

import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import dyn from 'next/dynamic'
import {
  BadgeCheck, BookOpen, Map as MapIcon, MapPin, Store, UtensilsCrossed,
  Phone, MessageCircle, Navigation, Star, Heart, Share2, LocateFixed, Check,
  ShoppingBag, Stethoscope, Plane, Home, Briefcase, GraduationCap, Car, Users, MoreHorizontal,
} from 'lucide-react'
import { BUSINESS_CATEGORIES, findParentOfChild } from '../../lib/businessCategories'
import { EmptyState } from '../../components/ui/kit'
import AdSlot from '../../components/ui/AdSlot'
import { haversineKm } from '../../lib/proximity'

/* PHASE 2b — uniform commerce-card grid. PHASE 3a — discovery features layered on:
   favourites (heart, DB-backed, mirrors saved-masjids), viewer-distance sort + geolocation
   ("Use my location"), a Saved filter, and per-card share. Honest data only — a chip, a
   distance, or a saved state appears only when it is real; nothing is fabricated. The map
   (DirectoryLiveMap) already clusters, so map-first browse is unchanged here. */

const DirectoryLiveMap = dyn(() => import('../../components/directory/DirectoryLiveMap'), {
  ssr: false,
  loading: () => <div style={{ height: 320, borderRadius: 14, background: '#101A38' }} />,
})

export type BusinessRow = {
  id: string
  name: string
  category: string
  locality: string | null
  city: string | null
  state: string | null
  latitude: string | null
  longitude: string | null
  phone: string | null
  whatsapp?: string | null
  website?: string | null
  status: string | null
  /** Effective tier slug (free|masjid|area|city|state|national), resolved server-side. */
  tier?: string | null
  /** Effective tier's display badge (MASJID/AREA/…); null for free/expired. */
  tierBadge?: string | null
  /** Effective tier's boundary level (city|state|national) — set only for boundary-scoped paid tiers. */
  tierBoundary?: string | null
  /** Effective tier's scope type (nearest|radius|boundary). */
  tierScope?: string | null
  halalAttested?: boolean | null
  muslimOwnedAttested?: boolean | null
  nearestMasjidSlug?: string | null
  nearestMasjidKm?: string | null
  imageUrl?: string | null
  logoUrl?: string | null
  bannerUrl?: string | null
  /** True when an owner already holds this listing. Boolean only — never the owner's id. */
  claimed?: boolean
}

// Card icon resolves via the Community+ taxonomy: a listing's stored category → its PARENT's
// lucide icon (the constant stores icon NAME strings; resolve name → component here). Unrecognized
// (legacy free-text) categories fall back to the generic Store icon.
const PARENT_ICON_COMPONENTS: Record<string, any> = {
  ShoppingBag, UtensilsCrossed, BookOpen, Heart, Stethoscope, Plane, Home, Briefcase, GraduationCap, Car, Users, MoreHorizontal,
}
function iconForCategory(category: string) {
  const parent = findParentOfChild(category)
  return (parent && PARENT_ICON_COMPONENTS[parent.icon]) || Store
}

/** Two-letter initials from a business name — the last-resort media fallback. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  const chars = (parts[0]?.[0] || '') + (parts[1]?.[0] || '')
  return (chars || name[0] || '?').toUpperCase()
}

/** Directions link: precise coords when known, else a maps search by name + place (always resolvable). */
function directionsHref(r: BusinessRow): string {
  const lat = Number(r.latitude), lng = Number(r.longitude)
  if (lat && lng) return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
  const q = encodeURIComponent([r.name, r.locality, r.city].filter(Boolean).join(', '))
  return `https://www.google.com/maps/search/?api=1&query=${q}`
}

/** wa.me link from a stored WhatsApp number, or null when there are no usable digits. */
function waHref(wa?: string | null): string | null {
  if (!wa) return null
  const digits = String(wa).replace(/[^\d]/g, '')
  return digits ? `https://wa.me/${digits}` : null
}

/** Reach-graded class for the paid-tier chip (gold family; national strongest → masjid lightest). */
function tierClass(tier?: string | null): string {
  switch (tier) {
    case 'national': return 'tier-national'
    case 'state': return 'tier-state'
    case 'city': return 'tier-city'
    case 'area': return 'tier-area'
    case 'masjid': return 'tier-masjid'
    default: return 'tier-area'
  }
}

/* ── The uniform card ──────────────────────────────────────────────────────────
   The card is position:relative so the share + heart tools sit as absolute siblings
   of the media <Link> (never nested inside an <a>). Media zone is a fixed height for
   ALL three render paths; the action row is bottom-pinned via the spacer, so every
   card is identical in outer size and internal alignment. */
function BusinessCard({
  r, featured = false, saved, authed, onToggleSave, viewerKm,
}: {
  r: BusinessRow
  featured?: boolean
  saved: boolean
  authed: boolean | null
  onToggleSave: (id: string) => void
  viewerKm?: number | null
}) {
  const [broken, setBroken] = useState(false)
  const [shared, setShared] = useState(false)
  const Icon = iconForCategory(r.category)

  const mediaUrl = r.bannerUrl || r.logoUrl || null
  const mediaKind: 'banner' | 'logo' | 'none' =
    r.bannerUrl ? 'banner' : r.logoUrl ? 'logo' : 'none'
  const showImg = mediaKind !== 'none' && !broken

  const paid = !!r.tier && r.tier !== 'free'
  const verified = r.status === 'verified'
  const showHalal = verified && !!r.halalAttested
  const showMuslimOwned = verified && !!r.muslimOwnedAttested

  const wa = waHref(r.whatsapp)
  const masjidKm = r.nearestMasjidKm != null && Number.isFinite(Number(r.nearestMasjidKm))
    ? Number(r.nearestMasjidKm) : null

  const share = async (e: React.MouseEvent) => {
    e.preventDefault()
    const url = typeof window !== 'undefined' ? `${window.location.origin}/business/${r.id}` : `/business/${r.id}`
    try {
      if (typeof navigator !== 'undefined' && (navigator as any).share) {
        await (navigator as any).share({ title: r.name, url })
      } else if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(url)
        setShared(true); setTimeout(() => setShared(false), 1500)
      }
    } catch { /* user cancelled the share sheet — nothing to do */ }
  }

  return (
    <div className={'bzc' + (featured ? ' bzc-featured' : '')} role="listitem">
      {/* Tools — siblings of the media link, so no interactive element is nested in an <a> */}
      <div className="bzc-tools">
        <button type="button" className={'bzc-tool' + (shared ? ' done' : '')} onClick={share} aria-label={`Share ${r.name}`}>
          {shared ? <Check size={14} /> : <Share2 size={14} />}
        </button>
        <button
          type="button"
          className={'bzc-tool' + (saved ? ' on' : '')}
          onClick={() => onToggleSave(r.id)}
          aria-pressed={saved}
          aria-label={saved ? `Remove ${r.name} from saved` : `Save ${r.name}`}
        >
          <Heart size={14} fill={saved ? 'currentColor' : 'none'} />
        </button>
      </div>

      {/* MEDIA ZONE — fixed height in all three states */}
      <Link href={`/business/${r.id}`} className="bzc-media" aria-label={`View ${r.name}`}>
        {showImg ? (
          <>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              className={mediaKind === 'banner' ? 'bzc-banner' : 'bzc-logo'}
              src={mediaUrl as string}
              alt={r.name}
              loading="lazy"
              onError={() => setBroken(true)}
            />
            {/* Tier ribbon on a banner only; verified lives in the trust row for all cards. */}
            {mediaKind === 'banner' && paid && (
              <span className={'bzc-ov bzc-ov-tier ' + tierClass(r.tier)}><Star size={9} /> {r.tierBadge || 'Partner'}</span>
            )}
          </>
        ) : (
          <span className="bzc-initials" aria-hidden="true">
            {r.logoUrl || r.bannerUrl ? initials(r.name) : <Icon size={22} />}
          </span>
        )}
      </Link>

      <div className="bzc-body">
        <Link href={`/business/${r.id}`} className="bzc-name">{r.name}</Link>
        <div className="bzc-cat">{r.category}</div>

        {/* TRUST ROW — fixed min-height; reserves its space even when empty */}
        <div className="bzc-trust">
          {paid && <span className={'bzc-chip ' + tierClass(r.tier)}><Star size={9} /> {r.tierBadge || 'Partner'}</span>}
          {verified && <span className="bzc-chip chip-verified"><BadgeCheck size={10} /> Verified</span>}
          {showHalal && <span className="bzc-chip chip-halal" title="Halal status attested by the verified owner">Halal</span>}
          {showMuslimOwned && <span className="bzc-chip chip-muslim" title="Attested by the verified owner">Muslim-owned</span>}
          {!paid && !verified && <span className="bzc-chip chip-listed">Listed</span>}
        </div>

        {/* META ROW — viewer distance ("away") only when we truly have coords; masjid distance is
            a separate, distinctly-labelled signal so the two are never confused. */}
        <div className="bzc-meta">
          <span className="bzc-loc"><MapPin size={11} /> {r.locality || r.city || '—'}</span>
          {viewerKm != null && <span className="bzc-dist away">{viewerKm.toFixed(1)} km away</span>}
          {masjidKm != null && <span className="bzc-dist masjid">{masjidKm.toFixed(1)} km to masjid</span>}
        </div>

        <div className="bzc-spacer" />

        {/* ACTION ROW — buttons flex to fill; each appears only when its data is real */}
        <div className="bzc-actions">
          {r.phone && (
            <a href={`tel:${r.phone}`} className="bzc-btn" aria-label={`Call ${r.name}`}>
              <Phone size={13} /> Call
            </a>
          )}
          {wa && (
            <a href={wa} target="_blank" rel="noopener noreferrer" className="bzc-btn bzc-btn-wa" aria-label={`WhatsApp ${r.name}`}>
              <MessageCircle size={13} /> Chat
            </a>
          )}
          <a href={directionsHref(r)} target="_blank" rel="noopener noreferrer" className="bzc-btn" aria-label={`Directions to ${r.name}`}>
            <Navigation size={13} /> Directions
          </a>
          {!r.claimed && (
            <Link href={`/business/${r.id}?claim=1`} className="bzc-btn bzc-btn-claim" aria-label={`Claim ${r.name}`}>
              Claim
            </Link>
          )}
        </div>
      </div>
    </div>
  )
}

/* ── "Near your masjid" — masjid-anchored discovery strip ──────────────────────
   Businesses near a masjid the viewer FOLLOWS, with a REAL distance to that masjid.
   Entirely client-side, no API changes: it fetches the viewer's followed masjids
   (/api/me/following, which returns full rows incl. lat/lng as decimal strings),
   then /api/businesses?lat&lng&radiusKm around the chosen masjid. That endpoint
   filters by Haversine but returns NO distance, so distance is recomputed here from
   the real coords via haversineKm — never fabricated. No ratings: the business list
   projection carries none, and a per-card review fetch would be N calls, so ratings
   are omitted (honest) rather than faked. Parent gates this on authed===true, so it
   never renders for logged-out visitors. */
type FollowedMasjid = { id: string; slug: string; name: string; lat: number; lng: number }
type NearBiz = {
  id: string; name: string; category: string; locality: string | null; city: string | null
  latitude: string | null; longitude: string | null; distKm: number
}
const NEAR_RADII = [5, 10, 25] as const
const fmtKm = (km: number) => `${km < 10 ? km.toFixed(1) : Math.round(km)} km`

function NearMasjidStrip() {
  // following: null = still loading; [] = signed in but follows nothing (coord-bearing)
  const [following, setFollowing] = useState<FollowedMasjid[] | null>(null)
  const [anchorId, setAnchorId] = useState<string | null>(null)
  const [radiusIdx, setRadiusIdx] = useState(0)
  const [biz, setBiz] = useState<NearBiz[] | null>(null) // null = fetching

  // Followed masjids (ordered most-recently-followed first by the API). Keep only those with
  // real coords — a masjid with no lat/lng can't anchor a proximity search.
  useEffect(() => {
    let cancelled = false
    fetch('/api/me/following')
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((d) => {
        if (cancelled) return
        const list: FollowedMasjid[] = (Array.isArray(d.masjids) ? d.masjids : [])
          .map((m: any) => ({
            id: String(m.id), slug: String(m.slug || ''),
            name: String(m.nameEnglish || m.name || 'your masjid'),
            lat: Number(m.latitude), lng: Number(m.longitude),
          }))
          .filter((m: FollowedMasjid) => Number.isFinite(m.lat) && Number.isFinite(m.lng) && m.lat !== 0 && m.lng !== 0)
        setFollowing(list)
        setAnchorId((prev) => prev ?? list[0]?.id ?? null)
      })
      .catch(() => { if (!cancelled) setFollowing([]) })
    return () => { cancelled = true }
  }, [])

  const anchor = useMemo(
    () => (following ?? []).find((m) => m.id === anchorId) ?? (following ?? [])[0] ?? null,
    [following, anchorId],
  )
  const radiusKm = NEAR_RADII[radiusIdx]

  // Businesses near the anchor. Refetch whenever the anchor or radius changes. Distance to the
  // ANCHOR is computed client-side (the API doesn't return it); rows without usable coords are
  // dropped rather than shown at an unknown distance. Sorted nearest-first.
  useEffect(() => {
    if (!anchor) { setBiz(null); return }
    let cancelled = false
    setBiz(null)
    fetch(`/api/businesses?lat=${anchor.lat}&lng=${anchor.lng}&radiusKm=${radiusKm}`)
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((d) => {
        if (cancelled) return
        const list: NearBiz[] = (Array.isArray(d.businesses) ? d.businesses : [])
          .map((b: any) => {
            const lat = Number(b.latitude), lng = Number(b.longitude)
            const distKm = Number.isFinite(lat) && Number.isFinite(lng) && lat && lng
              ? haversineKm(anchor.lat, anchor.lng, lat, lng) : NaN
            return {
              id: String(b.id), name: String(b.name || ''),
              category: String(b.cat || b.category || ''),
              locality: b.area ?? b.locality ?? null, city: b.city ?? null,
              latitude: b.latitude ?? null, longitude: b.longitude ?? null, distKm,
            } as NearBiz
          })
          .filter((b: NearBiz) => Number.isFinite(b.distKm))
          .sort((a: NearBiz, b: NearBiz) => a.distKm - b.distKm)
        setBiz(list)
      })
      .catch(() => { if (!cancelled) setBiz([]) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchor, radiusKm])

  // Still resolving the follow list → render nothing (avoids a skeleton that may then vanish for
  // a user who follows nobody). The business fetch below has its own in-place skeleton.
  if (following === null) return null

  let inner: React.ReactNode
  if (following.length === 0) {
    // Signed in, follows nothing → a helpful CTA, not a nag.
    inner = (
      <div className="nym-prompt">
        <span>Follow a masjid to discover halal businesses nearby.</span>
        <Link href="/masjids" className="nym-cta">Find masjids to follow →</Link>
      </div>
    )
  } else if (biz === null) {
    inner = (
      <div className="nym-strip" aria-busy="true" aria-label="Loading nearby businesses">
        {[0, 1, 2, 3].map((i) => <div key={i} className="nym-skel" aria-hidden="true" />)}
      </div>
    )
  } else if (biz.length > 0) {
    inner = (
      <div className="nym-strip" role="list">
        {biz.slice(0, 20).map((b) => {
          const Icon = iconForCategory(b.category)
          const dirHref = directionsHref({ latitude: b.latitude, longitude: b.longitude, name: b.name, locality: b.locality, city: b.city } as BusinessRow)
          return (
            <div className="nym-card" role="listitem" key={b.id}>
              <Link href={`/business/${b.id}`} className="nym-media" aria-label={`View ${b.name}`}>
                <Icon size={20} />
              </Link>
              <div className="nym-cbody">
                <Link href={`/business/${b.id}`} className="nym-name">{b.name}</Link>
                {b.category && <div className="nym-cat">{b.category}</div>}
                <div className="nym-dist"><MapPin size={11} /> {fmtKm(b.distKm)} from {anchor?.name}</div>
                <div className="nym-actions">
                  <Link href={`/business/${b.id}`} className="nym-btn">View</Link>
                  <a href={dirHref} target="_blank" rel="noopener noreferrer" className="nym-btn"><Navigation size={12} /> Directions</a>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    )
  } else {
    // Honest-empty: expand the radius before giving up; at max radius, a real "none yet" + register.
    const canExpand = radiusIdx < NEAR_RADII.length - 1
    inner = (
      <div className="nym-empty">
        {canExpand ? (
          <>
            <span>Nothing nearby yet within {radiusKm} km of {anchor?.name}.</span>
            <button type="button" className="nym-cta" onClick={() => setRadiusIdx((i) => Math.min(i + 1, NEAR_RADII.length - 1))}>
              Expand search to {NEAR_RADII[radiusIdx + 1]} km
            </button>
          </>
        ) : (
          <>
            <span>No businesses found near {anchor?.name} yet.</span>
            <Link href="/business/register" className="nym-cta">Register a business →</Link>
          </>
        )}
      </div>
    )
  }

  return (
    <section className="nym" aria-label="Near your masjid">
      <div className="nym-head">
        <MapPin size={13} /> Near your masjid
        {following.length > 0 && anchor && <span className="nym-sub">Close to {anchor.name}</span>}
      </div>

      {/* Multiple follows → a picker; default is the most-recently-followed (first). */}
      {following.length > 1 && (
        <div className="nym-picker" role="tablist" aria-label="Choose a followed masjid">
          {following.map((m) => (
            <button
              key={m.id} type="button" role="tab" aria-selected={m.id === anchor?.id}
              className={'nym-chip' + (m.id === anchor?.id ? ' on' : '')}
              onClick={() => { setAnchorId(m.id); setRadiusIdx(0) }}
            >
              {m.name}
            </button>
          ))}
        </div>
      )}

      {inner}

      <style jsx>{`
        .nym{display:grid;gap:10px;background:#fff;border:1px solid #e6e4dc;border-radius:14px;padding:13px 14px}
        .nym-head{display:flex;align-items:center;gap:7px;font-family:var(--f-mono);font-size:10px;letter-spacing:.14em;text-transform:uppercase;color:#0E7C61;font-weight:700}
        .nym-sub{margin-left:2px;font-family:inherit;letter-spacing:0;text-transform:none;font-size:11.5px;font-weight:500;color:#8a887f}

        /* Followed-masjid picker */
        .nym-picker{display:flex;gap:7px;flex-wrap:wrap}
        .nym-chip{border:1px solid #d8d6cd;border-radius:99px;background:#fbfbf8;padding:5px 12px;font-size:12px;font-family:inherit;color:#555;cursor:pointer;white-space:nowrap}
        .nym-chip.on{border-color:#0E7C61;color:#0E7C61;background:rgba(14,124,97,.07);font-weight:700}

        /* Horizontal scroll strip */
        .nym-strip{display:flex;gap:12px;overflow-x:auto;padding-bottom:4px;scroll-snap-type:x proximity}
        .nym-card{flex:0 0 auto;width:230px;display:flex;flex-direction:column;background:#fff;border:1px solid #e6e4dc;border-radius:13px;overflow:hidden;scroll-snap-align:start}
        .nym-media{display:flex;align-items:center;justify-content:center;height:54px;color:#0E7C61;background:linear-gradient(135deg,#f4f7f5,#eef2ef);text-decoration:none}
        .nym-cbody{display:flex;flex-direction:column;gap:3px;padding:10px 12px 11px}
        .nym-name{font-weight:700;font-size:13.5px;line-height:1.25;color:#1d1c16;text-decoration:none;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;min-height:34px}
        .nym-name:hover{color:#0E7C61}
        .nym-cat{font-size:11.5px;color:#8a887f;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
        .nym-dist{display:inline-flex;align-items:center;gap:4px;font-size:11.5px;font-weight:700;color:#0E7C61;margin-top:2px}
        .nym-actions{display:flex;gap:6px;margin-top:7px}
        .nym-btn{flex:1;display:inline-flex;align-items:center;justify-content:center;gap:5px;font-size:11.5px;font-weight:700;color:#0E7C61;background:rgba(14,124,97,.06);border:1px solid rgba(14,124,97,.22);border-radius:8px;padding:6px 6px;text-decoration:none;white-space:nowrap;cursor:pointer}
        .nym-btn:hover{background:rgba(14,124,97,.12)}

        /* Loading skeleton — same footprint as a card, so no layout jump */
        .nym-skel{flex:0 0 auto;width:230px;height:148px;border-radius:13px;border:1px solid #eceae2;background:linear-gradient(90deg,#f6f5f0,#fbfbf8,#f6f5f0);background-size:200% 100%;animation:nymShimmer 1.2s ease-in-out infinite}
        @keyframes nymShimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}

        /* Prompt + honest-empty share a compact inline layout */
        .nym-prompt,.nym-empty{display:flex;align-items:center;gap:10px;flex-wrap:wrap;font-size:13px;color:#6b6b66}
        .nym-cta{font-weight:700;color:#0E7C61;text-decoration:none;background:rgba(14,124,97,.06);border:1px solid rgba(14,124,97,.22);border-radius:9px;padding:6px 12px;cursor:pointer;font-size:12.5px;font-family:inherit}
        .nym-cta:hover{background:rgba(14,124,97,.12)}

        @media(max-width:520px){
          .nym-card,.nym-skel{width:200px}
        }
      `}</style>
    </section>
  )
}

export default function BusinessDirectoryClient({ rows }: { rows: BusinessRow[] }) {
  const [city, setCity] = useState('All')
  const [parentCat, setParentCat] = useState('All')
  const [childCat, setChildCat] = useState('All')
  const [locality, setLocality] = useState('All')
  const [showMap, setShowMap] = useState(false)

  // Discovery state (Phase 3a)
  const [sort, setSort] = useState<'featured' | 'nearest'>('featured')
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null)
  const [geo, setGeo] = useState<'idle' | 'locating' | 'denied'>('idle')
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set())
  const [authed, setAuthed] = useState<boolean | null>(null) // null = unknown until first fetch
  const [onlySaved, setOnlySaved] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  // Load the viewer's favourited ids once. 200 → signed in (mark hearts); anything else
  // (401 unauth, or 403 while the module is dark) → treat as "not signed in", hearts prompt.
  useEffect(() => {
    let cancelled = false
    fetch('/api/businesses/favourites')
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((d) => { if (!cancelled) { setSavedIds(new Set<string>(d.ids || [])); setAuthed(true) } })
      .catch(() => { if (!cancelled) setAuthed(false) })
    return () => { cancelled = true }
  }, [])

  const toggleSave = async (id: string) => {
    if (authed === false) { setNotice('Sign in to save businesses to your account.'); return }
    const wasSaved = savedIds.has(id)
    // optimistic
    setSavedIds((prev) => { const n = new Set(prev); wasSaved ? n.delete(id) : n.add(id); return n })
    try {
      const res = await fetch('/api/businesses/favourites', {
        method: wasSaved ? 'DELETE' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ businessId: id }),
      })
      if (res.status === 401 || res.status === 403) {
        setAuthed(false); setNotice('Sign in to save businesses to your account.')
        setSavedIds((prev) => { const n = new Set(prev); wasSaved ? n.add(id) : n.delete(id); return n })
        return
      }
      if (!res.ok) throw new Error('save failed')
    } catch {
      setSavedIds((prev) => { const n = new Set(prev); wasSaved ? n.add(id) : n.delete(id); return n })
      setNotice('Could not update saved state right now.')
    }
  }

  const locate = () => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) { setGeo('denied'); return }
    setGeo('locating')
    navigator.geolocation.getCurrentPosition(
      (pos) => { setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setGeo('idle'); setSort('nearest') },
      () => setGeo('denied'),
      { enableHighAccuracy: true, timeout: 8000 },
    )
  }

  const cities = useMemo(() => ['All', ...new Set(rows.map((r) => r.city).filter(Boolean) as string[])], [rows])
  // 2-level category browse from the Community+ taxonomy. `parentCat`/`childCat` are the selected
  // parent/child LABELS ('All' = unset). The child <select> is populated from the chosen parent.
  const selectedParent = useMemo(() => BUSINESS_CATEGORIES.find((p) => p.label === parentCat), [parentCat])
  const childOptions = selectedParent?.children ?? []
  const localities = useMemo(
    () => ['All', ...new Set(rows.filter((r) => city === 'All' || r.city === city).map((r) => r.locality).filter(Boolean) as string[])].slice(0, 40),
    [rows, city],
  )

  const filtered = useMemo(() => rows.filter((r) => {
    // Category predicate: 'All' parent → no filter (so legacy/unrecognized categories STILL show);
    // parent + child → exact child match; parent only → any child of that parent.
    const catOk =
      parentCat === 'All' ? true
      : childCat !== 'All' ? r.category === childCat
      : (selectedParent?.children.some((c) => c.label === r.category) ?? false)
    return (city === 'All' || r.city === city) && catOk && (locality === 'All' || r.locality === locality)
  }), [rows, city, parentCat, childCat, selectedParent, locality])

  // ── Location-aware featured strip (unchanged from 2b) ──
  const viewerCity = city !== 'All' ? city : null
  const viewerState = useMemo(() => {
    if (!viewerCity) return null
    return rows.find((r) => r.city === viewerCity && r.state)?.state ?? null
  }, [rows, viewerCity])

  const featured = useMemo(() => {
    const boundaryPartners = rows.filter((r) => r.tier && r.tier !== 'free' && r.tierBoundary)
    return boundaryPartners.filter((r) => {
      if (r.tierBoundary === 'national') return true
      if (r.tierBoundary === 'state') return !!viewerState && r.state === viewerState
      if (r.tierBoundary === 'city') return !!viewerCity && r.city === viewerCity
      return false
    }).slice(0, 4)
  }, [rows, viewerCity, viewerState])
  const featuredIds = useMemo(() => new Set(featured.map((f) => f.id)), [featured])

  /** Attach a live viewer-distance to each row (null unless we actually have coords + biz coords). */
  const viewerKmOf = (r: BusinessRow): number | null => {
    if (!coords) return null
    const lat = Number(r.latitude), lng = Number(r.longitude)
    return lat && lng ? haversineKm(coords.lat, coords.lng, lat, lng) : null
  }

  // Main grid = filtered − featured, optionally narrowed to Saved, then ordered.
  // Default order preserves the Phase-1 effective-tier ranking (server order). "Nearest" is an
  // opt-in re-sort by live viewer distance; rows with no coords sink to the end (never faked).
  const gridItems = useMemo(() => {
    let base = filtered.filter((r) => !featuredIds.has(r.id))
    if (onlySaved) base = base.filter((r) => savedIds.has(r.id))
    const withKm = base.map((r) => ({ r, viewerKm: viewerKmOf(r) }))
    if (sort === 'nearest' && coords) {
      withKm.sort((a, b) => {
        if (a.viewerKm == null && b.viewerKm == null) return 0
        if (a.viewerKm == null) return 1
        if (b.viewerKm == null) return -1
        return a.viewerKm - b.viewerKm
      })
    }
    return withKm
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, featuredIds, onlySaved, savedIds, sort, coords])

  const mapRecords = filtered
    .filter((r) => Number(r.latitude) && Number(r.longitude))
    .slice(0, 400)
    .map((r) => ({
      slug: r.id, name: r.name, city: r.city,
      lat: Number(r.latitude), lng: Number(r.longitude),
      verified: r.status === 'verified',
      href: `/business/${r.id}`, ctaLabel: 'View business →', meta: r.category,
    }))

  return (
    <div className="bizu">
      {notice && (
        <div className="bizu-notice" role="status">
          <span>{notice}</span>
          <Link href="/login" className="bizu-notice-cta">Sign in</Link>
          <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      {featured.length > 0 && (
        <section className="bizu-featured" aria-label="Featured partners">
          <div className="bizu-featured-head">
            <BadgeCheck size={13} /> Featured partners
            <span className="bizu-featured-sub">Sponsored · {viewerCity ? `relevant to ${viewerCity}` : 'nationwide'}</span>
          </div>
          <div className="bizu-grid" role="list">
            {featured.map((r) => (
              <BusinessCard
                key={r.id} r={r} featured
                saved={savedIds.has(r.id)} authed={authed} onToggleSave={toggleSave}
                viewerKm={viewerKmOf(r)}
              />
            ))}
          </div>
        </section>
      )}

      <div className="bizu-bar" role="toolbar" aria-label="Business filters">
        <select value={city} onChange={(e) => { setCity(e.target.value); setLocality('All') }} aria-label="City">
          {cities.map((c) => <option key={c}>{c}</option>)}
        </select>
        <select value={parentCat} onChange={(e) => { setParentCat(e.target.value); setChildCat('All') }} aria-label="Category">
          <option value="All">All categories</option>
          {BUSINESS_CATEGORIES.map((p) => <option key={p.id} value={p.label}>{p.label}</option>)}
        </select>
        {parentCat !== 'All' ? (
          <select value={childCat} onChange={(e) => setChildCat(e.target.value)} aria-label="Subcategory">
            <option value="All">All in {parentCat}</option>
            {childOptions.map((c) => <option key={c.id} value={c.label}>{c.label}</option>)}
          </select>
        ) : null}
        <select value={locality} onChange={(e) => setLocality(e.target.value)} aria-label="Locality">
          {localities.map((c) => <option key={c}>{c}</option>)}
        </select>

        {/* Sort — "Nearest" is enabled only once we have viewer coords (else it can't be honest). */}
        <select value={sort} onChange={(e) => setSort(e.target.value as 'featured' | 'nearest')} aria-label="Sort by">
          <option value="featured">Sort: Featured</option>
          <option value="nearest" disabled={!coords}>Sort: Nearest{coords ? '' : ' (locate first)'}</option>
        </select>

        <button type="button" className={'bizu-geo' + (coords ? ' on' : '')} onClick={locate} disabled={geo === 'locating'}>
          <LocateFixed size={13} /> {geo === 'locating' ? 'Locating…' : coords ? 'Using your location' : 'Use my location'}
        </button>

        <button type="button" className={'bizu-saved-toggle' + (onlySaved ? ' on' : '')} onClick={() => setOnlySaved((v) => !v)} aria-pressed={onlySaved}>
          <Heart size={13} fill={onlySaved ? 'currentColor' : 'none'} /> Saved{savedIds.size ? ` (${savedIds.size})` : ''}
        </button>

        <button type="button" className={'bizu-maptoggle' + (showMap ? ' on' : '')} onClick={() => setShowMap((v) => !v)}>
          <MapIcon size={13} /> Map view
        </button>
        <span className="bizu-count">{gridItems.length.toLocaleString('en-IN')} businesses</span>
      </div>

      {geo === 'denied' && (
        <div className="bizu-geo-msg" role="status">Location unavailable — allow location access to sort by distance. All listings still shown.</div>
      )}

      {/* "Near your masjid" — masjid-anchored discovery, shown only to signed-in viewers. */}
      {authed === true && <NearMasjidStrip />}

      {showMap && mapRecords.length > 0 && (
        <DirectoryLiveMap
          records={mapRecords} height={320}
          headerLabel={`${city === 'All' ? 'All cities' : city} businesses`}
          headerChips={[{ k: 'on map', v: String(mapRecords.length) }]}
          onLocate={(lat, lng) => { setCoords({ lat, lng }); setSort('nearest') }}
        />
      )}

      {/* De-emphasised: a slim, clearly-labelled ad rail — not a loud banner. */}
      <div className="bizu-ad">
        <span className="bizu-ad-label">Advertisement</span>
        <AdSlot placement="business_list" variant="leaderboard" city={city === 'All' ? undefined : city} />
      </div>

      {gridItems.length ? (
        <div className="bizu-grid" role="list">
          {gridItems.slice(0, 120).map(({ r, viewerKm }) => (
            <BusinessCard
              key={r.id} r={r}
              saved={savedIds.has(r.id)} authed={authed} onToggleSave={toggleSave}
              viewerKm={viewerKm}
            />
          ))}
        </div>
      ) : (
        <EmptyState
          icon={<Store size={20} />}
          title={onlySaved ? 'No saved businesses yet' : 'No approved businesses match these filters yet'}
          body={onlySaved
            ? 'Tap the heart on any listing to save it here for quick access.'
            : 'The directory is human-curated — records appear as they pass review. Run a business? Register free.'}
          cta={onlySaved ? { label: 'Browse all businesses', href: '#businesses' } : { label: 'Register your business', href: '/business/register' }}
        />
      )}

      <style jsx>{`
        .bizu{display:grid;gap:16px}

        /* Sign-in / error notice */
        .bizu-notice{display:flex;align-items:center;gap:10px;background:#FFF9E9;border:1px solid rgba(212,175,55,.5);border-radius:11px;padding:9px 13px;font-size:13px;color:#6b5a1e}
        .bizu-notice-cta{margin-left:auto;font-weight:700;color:#0E7C61;text-decoration:none}
        .bizu-notice button{background:none;border:0;font-size:17px;line-height:1;color:#9a988e;cursor:pointer}

        /* Featured strip */
        .bizu-featured{display:grid;gap:11px}
        .bizu-featured-head{display:flex;align-items:center;gap:8px;font-family:var(--f-mono);font-size:10px;letter-spacing:.14em;text-transform:uppercase;color:#9A7B1E;font-weight:700}
        .bizu-featured-sub{margin-left:2px;font-family:inherit;letter-spacing:0;text-transform:none;font-size:11.5px;font-weight:500;color:#a08a4e}

        /* Filter bar */
        .bizu-bar{display:flex;gap:8px;flex-wrap:wrap;align-items:center;background:#fff;border:1px solid #e6e4dc;border-radius:12px;padding:9px 11px}
        .bizu-bar select{border:1px solid #d8d6cd;border-radius:9px;background:#fbfbf8;padding:8px 11px;font-size:13px;font-family:inherit;color:#333;cursor:pointer}
        .bizu-geo,.bizu-saved-toggle,.bizu-maptoggle{display:inline-flex;align-items:center;gap:6px;border:1px solid #d8d6cd;border-radius:9px;background:#fbfbf8;padding:8px 12px;font-size:12.5px;font-family:inherit;color:#555;cursor:pointer}
        .bizu-geo:disabled{opacity:.6;cursor:default}
        .bizu-geo.on{border-color:#0E7C61;color:#0E7C61;background:rgba(14,124,97,.06)}
        .bizu-saved-toggle.on{border-color:#d4736f;color:#c14b46;background:rgba(209,75,70,.06)}
        .bizu-maptoggle.on{border-color:#0E7C61;color:#0E7C61;background:rgba(14,124,97,.06)}
        .bizu-count{margin-left:auto;font-size:12.5px;color:#8a887f}
        .bizu-geo-msg{font-size:12.5px;color:#8a887f;background:#faf9f5;border:1px solid #eceae2;border-radius:10px;padding:8px 12px}

        /* Slim, labelled ad rail */
        .bizu-ad{display:flex;flex-direction:column;align-items:center;gap:3px;opacity:.9}
        .bizu-ad-label{font-family:var(--f-mono);font-size:8.5px;letter-spacing:.16em;text-transform:uppercase;color:#b3b1a6}

        /* Uniform card grid: 4 / 2 / 1 via auto-fill; stretch keeps every card the same height */
        .bizu-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:14px;align-items:stretch}

        /* ── Uniform card ── */
        .bizu :global(.bzc){position:relative;display:flex;flex-direction:column;height:100%;background:#fff;border:1px solid #e6e4dc;border-radius:14px;overflow:hidden}
        .bizu :global(.bzc-featured){border-color:rgba(212,175,55,.55);box-shadow:0 2px 12px rgba(212,175,55,.14)}

        /* Tools (share + heart) */
        .bizu :global(.bzc-tools){position:absolute;top:7px;right:7px;z-index:2;display:flex;gap:5px}
        .bizu :global(.bzc-tool){width:28px;height:28px;display:grid;place-items:center;border-radius:99px;border:1px solid #e6e4dc;background:rgba(255,255,255,.92);color:#6b6b66;cursor:pointer;backdrop-filter:blur(2px);padding:0}
        .bizu :global(.bzc-tool:hover){color:#1d1c16}
        .bizu :global(.bzc-tool.on){color:#c14b46;border-color:rgba(209,75,70,.4)}
        .bizu :global(.bzc-tool.done){color:#0E7C61;border-color:rgba(14,124,97,.4)}

        /* Media zone — SAME height for banner / logo / initials */
        .bizu :global(.bzc-media){position:relative;display:block;height:76px;background:#f4f2ec;overflow:hidden}
        .bizu :global(.bzc-banner){width:100%;height:100%;object-fit:cover;display:block}
        .bizu :global(.bzc-logo){width:100%;height:100%;object-fit:contain;padding:10px;display:block;background:#fff}
        .bizu :global(.bzc-initials){width:100%;height:100%;display:flex;align-items:center;justify-content:center;font-family:var(--f-mono);font-size:22px;font-weight:800;color:#0E7C61;background:linear-gradient(135deg,#f4f7f5,#eef2ef)}
        .bizu :global(.bzc-ov){position:absolute;top:7px;left:7px;display:inline-flex;align-items:center;gap:4px;font-size:9px;font-weight:800;letter-spacing:.06em;text-transform:uppercase;border-radius:99px;padding:3px 7px;backdrop-filter:blur(2px)}

        /* Body */
        .bizu :global(.bzc-body){display:flex;flex-direction:column;flex:1;padding:11px 13px 12px}
        .bizu :global(.bzc-name){font-weight:700;font-size:14px;line-height:1.25;color:#1d1c16;text-decoration:none;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;min-height:35px}
        .bizu :global(.bzc-name:hover){color:#0E7C61}
        .bizu :global(.bzc-cat){margin-top:3px;font-size:12px;color:#8a887f;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}

        /* Trust row — reserves height even when empty */
        .bizu :global(.bzc-trust){display:flex;flex-wrap:wrap;gap:5px;margin-top:8px;min-height:22px}
        .bizu :global(.bzc-chip){display:inline-flex;align-items:center;gap:3px;font-size:10px;font-weight:700;letter-spacing:.03em;border:1px solid #ddd;border-radius:99px;padding:2px 8px;color:#8a887f;white-space:nowrap}
        .bizu :global(.chip-verified){color:#0E7C61;border-color:rgba(14,124,97,.4);background:rgba(14,124,97,.06)}
        .bizu :global(.chip-halal){color:#0E7C61;border-color:rgba(14,124,97,.35)}
        .bizu :global(.chip-muslim){color:#5b5a52;border-color:#dcdad0}
        .bizu :global(.chip-listed){color:#9a988e;border-color:#e4e2d9}
        .bizu :global(.tier-national){color:#fff;background:#9A7B1E;border-color:#9A7B1E}
        .bizu :global(.tier-state){color:#7a5f12;background:rgba(212,175,55,.2);border-color:rgba(212,175,55,.55)}
        .bizu :global(.tier-city){color:#9A7B1E;background:rgba(212,175,55,.12);border-color:rgba(212,175,55,.45)}
        .bizu :global(.tier-area),.bizu :global(.tier-masjid){color:#9A7B1E;background:rgba(212,175,55,.08);border-color:rgba(212,175,55,.35)}
        .bizu :global(.bzc-ov-tier.tier-national){color:#fff;background:rgba(154,123,30,.92)}
        .bizu :global(.bzc-ov-tier.tier-state),.bizu :global(.bzc-ov-tier.tier-city),.bizu :global(.bzc-ov-tier.tier-area),.bizu :global(.bzc-ov-tier.tier-masjid){color:#7a5f12;background:rgba(255,249,230,.94)}

        /* Meta row */
        .bizu :global(.bzc-meta){display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:8px;font-size:11.5px;color:#8a887f}
        .bizu :global(.bzc-loc){display:inline-flex;align-items:center;gap:4px;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        .bizu :global(.bzc-dist.away){color:#0E7C61;font-weight:700;white-space:nowrap}
        .bizu :global(.bzc-dist.masjid){color:#9a988e;white-space:nowrap}

        /* Spacer pins actions to the bottom */
        .bizu :global(.bzc-spacer){flex:1;min-height:10px}

        /* Action row */
        .bizu :global(.bzc-actions){display:flex;gap:6px}
        .bizu :global(.bzc-btn){flex:1;display:inline-flex;align-items:center;justify-content:center;gap:5px;font-size:11.5px;font-weight:700;color:#0E7C61;background:rgba(14,124,97,.06);border:1px solid rgba(14,124,97,.22);border-radius:9px;padding:7px 6px;text-decoration:none;white-space:nowrap;cursor:pointer}
        .bizu :global(.bzc-btn:hover){background:rgba(14,124,97,.12)}
        .bizu :global(.bzc-btn-wa){color:#128C7E;background:rgba(18,140,126,.06);border-color:rgba(18,140,126,.22)}
        .bizu :global(.bzc-btn-wa:hover){background:rgba(18,140,126,.12)}
        .bizu :global(.bzc-btn-claim){flex:0 0 auto;color:#9A7B1E;background:rgba(212,175,55,.08);border-color:rgba(212,175,55,.35)}
        .bizu :global(.bzc-btn-claim:hover){background:rgba(212,175,55,.16)}

        @media(max-width:520px){
          .bizu-grid{grid-template-columns:1fr}
          .bizu-count{margin-left:0}
        }
      `}</style>
    </div>
  )
}
